import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session lives in an httpOnly cookie set by the backend, so nothing
// sensitive is readable from JS. Every call must send credentials.
const auth = { withCredentials: true };

const errText = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || "").join(" ");
  return String(detail);
};

const CSV_HEADERS = [
  "Received",
  "Name",
  "Email",
  "Phone",
  "Preferred contact",
  "Interested in",
  "Preferred timing",
  "Offer",
  "Came from",
  "Page",
  "Message",
];

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

function downloadCsv(enquiries) {
  const rows = enquiries.map((e) => [
    new Date(e.created_at).toLocaleString("en-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
    e.name,
    e.email,
    e.phone,
    e.preferred_contact,
    (e.services || []).join("; "),
    (e.preferred_timing || []).join("; "),
    e.source_offer || "",
    e.source_placement || "Direct",
    e.source_page || "",
    e.message || "",
  ]);
  const csv = [CSV_HEADERS, ...rows]
    .map((r) => r.map(csvCell).join(","))
    .join("\r\n");
  const url = URL.createObjectURL(
    new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `alae-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Stat = ({ label, rows }) => (
  <div className="border border-umber/15 bg-paper p-6">
    <div className="font-meta text-forest mb-4">{label}</div>
    {rows.length === 0 ? (
      <p className="text-sm text-umber/60">Nothing yet.</p>
    ) : (
      <ul className="space-y-2.5">
        {rows.map(([k, v]) => (
          <li key={k} className="flex items-baseline justify-between gap-4">
            <span className="text-sm text-umber/80">{k}</span>
            <span className="font-display text-xl text-umber">{v}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function AdminEnquiries() {
  const [signedIn, setSignedIn] = useState(null); // null = checking session
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Enquiries | Alae Dental";
    let meta = document.head.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex, nofollow");
    return () => meta.setAttribute("content", "index, follow");
  }, []);

  useEffect(() => {
    if (signedIn === false) {
      setData(null);
      return;
    }
    axios
      .get(`${API}/admin/enquiries`, auth)
      .then((r) => {
        setData(r.data);
        setSignedIn(true);
      })
      .catch((e) => {
        setData(null);
        setSignedIn(false);
        if (e.response?.status !== 401) {
          toast.error("Could not load enquiries.");
        }
      });
  }, [signedIn]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/login`, creds, auth);
      setCreds({ email: "", password: "" });
      setSignedIn(null);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (signedIn === false) {
    return (
      <main
        data-testid="admin-login-page"
        className="bg-linen paper-grain min-h-[80svh] flex items-center py-24"
      >
        <div className="max-w-md w-full mx-auto px-6">
          <div className="font-meta text-forest mb-5">Practice Access</div>
          <h1 className="font-display text-4xl leading-[1.1] text-umber">
            Sign in to view{" "}
            <span className="font-display-italic">enquiries.</span>
          </h1>
          <form
            onSubmit={onLogin}
            data-testid="admin-login-form"
            className="mt-10 space-y-6"
          >
            <div>
              <label className="font-meta text-umber/60 block mb-2">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                required
                className="alae-input"
                value={creds.email}
                onChange={(e) => setCreds({ ...creds, email: e.target.value })}
              />
            </div>
            <div>
              <label className="font-meta text-umber/60 block mb-2">
                Password
              </label>
              <input
                data-testid="admin-login-password"
                type="password"
                required
                className="alae-input"
                value={creds.password}
                onChange={(e) =>
                  setCreds({ ...creds, password: e.target.value })
                }
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy}
              className="bg-forest text-paper rounded-full px-9 py-3.5 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 disabled:opacity-60 min-h-[44px]"
            >
              {busy ? "Signing in" : "Sign In"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-enquiries-page"
      className="bg-linen paper-grain min-h-[80svh] py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="font-meta text-forest mb-4">Enquiries</div>
            <h1 className="font-display text-4xl md:text-5xl leading-[1.1] text-umber">
              What the promo is{" "}
              <span className="font-display-italic">pulling in.</span>
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              data-testid="admin-export-csv"
              disabled={!data || data.total_enquiries === 0}
              onClick={() => {
                downloadCsv(data.enquiries);
                toast.success("CSV downloaded.");
              }}
              className="inline-flex items-center gap-2.5 bg-forest text-paper rounded-full px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-sage transition-colors disabled:opacity-50 min-h-[44px]"
            >
              <Download size={14} strokeWidth={1.8} />
              Download CSV
            </button>
            <button
              type="button"
              data-testid="admin-signout"
              onClick={async () => {
                try {
                  await axios.post(`${API}/auth/logout`, {}, auth);
                } finally {
                  setData(null);
                  setSignedIn(false);
                }
              }}
              className="border border-umber/40 text-umber rounded-full px-6 py-3 text-[11px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-colors min-h-[44px]"
            >
              Sign Out
            </button>
          </div>
        </div>

        {!data ? (
          <p className="mt-12 text-sm text-umber/60">Loading.</p>
        ) : (
          <>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="border border-umber/15 bg-paper p-6">
                <div className="font-meta text-forest mb-4">Totals</div>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm text-umber/80">Enquiries</span>
                  <span
                    data-testid="admin-total-enquiries"
                    className="font-display text-3xl text-umber"
                  >
                    {data.total_enquiries}
                  </span>
                </div>
                <div className="mt-3 flex items-baseline justify-between gap-4">
                  <span className="text-sm text-umber/80">Booking clicks</span>
                  <span className="font-display text-3xl text-umber">
                    {data.total_booking_clicks}
                  </span>
                </div>
              </div>
              <Stat
                label="Enquiries by offer"
                rows={Object.entries(data.enquiries_by_offer)}
              />
              <Stat
                label="Enquiries by placement"
                rows={Object.entries(data.enquiries_by_placement)}
              />
              <Stat
                label="Booking clicks by offer"
                rows={Object.entries(data.booking_clicks_by_offer)}
              />
            </div>

            <div className="mt-14 overflow-x-auto border border-umber/15 bg-paper">
              <table
                data-testid="admin-enquiries-table"
                className="w-full text-sm text-left"
              >
                <thead>
                  <tr className="border-b border-umber/15 text-[10px] tracking-[0.2em] uppercase text-umber/60">
                    <th className="px-5 py-4 font-normal">Received</th>
                    <th className="px-5 py-4 font-normal">Name</th>
                    <th className="px-5 py-4 font-normal">Contact</th>
                    <th className="px-5 py-4 font-normal">Interested in</th>
                    <th className="px-5 py-4 font-normal">Offer</th>
                    <th className="px-5 py-4 font-normal">Came from</th>
                  </tr>
                </thead>
                <tbody>
                  {data.enquiries.map((e) => (
                    <tr
                      key={e.id}
                      className="border-b border-umber/10 last:border-0 align-top"
                    >
                      <td className="px-5 py-4 text-umber/70 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString("en-CA", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </td>
                      <td className="px-5 py-4 text-umber">{e.name}</td>
                      <td className="px-5 py-4 text-umber/80">
                        <div>{e.email}</div>
                        <div className="text-umber/60">{e.phone}</div>
                        <div className="text-[11px] tracking-[0.14em] uppercase text-umber/50 mt-1">
                          Prefers {e.preferred_contact}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-umber/80">
                        {(e.services || []).join(", ") || "Not specified"}
                      </td>
                      <td className="px-5 py-4 text-forest">
                        {e.source_offer || "-"}
                      </td>
                      <td className="px-5 py-4 text-umber/70">
                        {e.source_placement || "Direct"}
                        {e.source_page ? (
                          <div className="text-umber/50">{e.source_page}</div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
